import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

import Badge from '@/components/Badge';
import IconicButton from '@/components/IconicButton';
import Stack from '@/components/Stack';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import { TableRow, TableCell } from '@/elements/Table';

import { Certificate } from './api';

type Props = {
  certificate: Certificate;
  onEdit: (certificate: Certificate) => void;
  onDelete: (certificate: Certificate) => void;
};

const usageLabel = (certificate: Certificate) => {
  const { usage } = certificate;
  const parts: string[] = [];
  if (usage.workers > 0) {
    parts.push(`${usage.workers} worker${usage.workers === 1 ? '' : 's'}`);
  }
  if (usage.customers > 0) {
    parts.push(`${usage.customers} client${usage.customers === 1 ? '' : 's'}`);
  }
  if (usage.upcoming_jobs > 0) {
    parts.push(
      `${usage.upcoming_jobs} upcoming job${
        usage.upcoming_jobs === 1 ? '' : 's'
      }`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'Not in use';
};

const BadgeRowItem = ({ certificate, onEdit, onDelete }: Props) => {
  const isBadge = certificate.certificate_type === 'BADGE';

  return (
    <TableRow>
      <TableCell>
        {certificate.image_url ? (
          <img
            alt=""
            src={certificate.image_url}
            style={{ width: 32, height: 32, objectFit: 'contain' }}
          />
        ) : (
          <Small color="lighter">—</Small>
        )}
      </TableCell>
      <TableCell>
        <TextStack>
          <Body weight="medium">{certificate.name}</Body>
          {certificate.display_name_es && (
            <Small color="light">{certificate.display_name_es}</Small>
          )}
        </TextStack>
      </TableCell>
      <TableCell>
        <Badge
          label={isBadge ? 'Badge' : 'Certificate'}
          status={isBadge ? 'theme' : 'neutral'}
        />
      </TableCell>
      <TableCell>
        <Small color={certificate.usage.workers > 0 ? 'default' : 'lighter'}>
          {usageLabel(certificate)}
        </Small>
      </TableCell>
      <TableCell>
        {certificate.deleted ? (
          <Badge label="Deleted" status="danger" />
        ) : (
          <Badge label="Active" status="success" />
        )}
      </TableCell>
      <TableCell>
        <Stack gap={4} justify="end">
          <IconicButton
            a11yLabel={`Edit ${certificate.name}`}
            appearance="plain"
            icon={faPencil}
            size="sm"
            onClick={() => onEdit(certificate)}
          />
          {!certificate.deleted && (
            <IconicButton
              a11yLabel={`Delete ${certificate.name}`}
              appearance="plain"
              icon={faTrash}
              size="sm"
              onClick={() => onDelete(certificate)}
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default BadgeRowItem;
